import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { InputBase, InputBaseProps } from '@mui/material';

/**
 * Whole-dollar text input that keeps thousands separators WHILE typing without
 * the caret-jump problems of a naive controlled input.
 *
 * The naive pattern (parse → store number → re-render `toLocaleString`) throws
 * the caret to the end on every keystroke, so editing the middle of "1,234,567"
 * silently continues at the end, and Backspace over a comma does nothing. Here:
 *  - the formatted text is local state while focused; the caret is restored by
 *    counting the digits to its left, so commas appearing/disappearing don't
 *    shift it;
 *  - Backspace/Delete across a comma removes the adjacent digit instead;
 *  - the parsed whole-dollar number still flows out on every keystroke.
 *
 * `emptyValue` is what an empty box means to the caller (null or 0).
 * `showZeroAsEmpty` renders 0 as an empty box (so a placeholder can show).
 */
export interface CurrencyTextInputProps extends Omit<InputBaseProps, 'value' | 'onChange' | 'type'> {
  value: number | null | undefined;
  onChange: (next: number | null) => void;
  emptyValue?: number | null;
  showZeroAsEmpty?: boolean;
}

const isDigit = (ch: string) => ch >= '0' && ch <= '9';

const formatDigits = (digits: string): string => (digits === '' ? '' : Number(digits).toLocaleString('en-US'));

const formatValue = (value: number | null | undefined, showZeroAsEmpty: boolean): string => {
  if (value == null || Number.isNaN(Number(value))) return '';
  const rounded = Math.round(Number(value));
  if (rounded === 0 && showZeroAsEmpty) return '';
  return rounded.toLocaleString('en-US');
};

const digitsBefore = (s: string, pos: number): number => {
  let n = 0;
  for (let i = 0; i < pos && i < s.length; i++) if (isDigit(s[i])) n++;
  return n;
};

/** Index just after the n-th digit of `formatted` (before any following comma). */
const caretAfterDigits = (formatted: string, n: number): number => {
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (seen === n) return i;
    if (isDigit(formatted[i])) seen++;
  }
  return formatted.length;
};

/**
 * Pure core of a keystroke: given the raw (comma-laden) text and caret index,
 * return the re-formatted text, the caret index that keeps the same digits to
 * its left, and the digit string. Exported for tests.
 */
export const computeNextCurrencyText = (
  raw: string,
  caret: number
): { formatted: string; caret: number; digits: string } => {
  let before = digitsBefore(raw, caret);
  let digits = raw.replace(/\D/g, '');
  // Drop leading zeros ("05" → "5"), keeping the caret on the same digit.
  const stripped = digits.replace(/^0+(?=\d)/, '');
  before = Math.max(0, before - (digits.length - stripped.length));
  digits = stripped;
  const formatted = formatDigits(digits);
  return { formatted, caret: caretAfterDigits(formatted, before), digits };
};

export const CurrencyTextInput: React.FC<CurrencyTextInputProps> = ({
  value,
  onChange,
  emptyValue = null,
  showZeroAsEmpty = false,
  onFocus,
  onBlur,
  inputRef: inputRefProp,
  inputProps,
  ...rest
}) => {
  const inputEl = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatValue(value, showZeroAsEmpty));
  const pendingCaret = useRef<number | null>(null);

  // Not focused → always mirror the prop. Focused → mirror only if the prop
  // diverged from what we're showing (e.g. a form reset while the field is active).
  useEffect(() => {
    const fromProp = formatValue(value, showZeroAsEmpty);
    if (!focused) {
      setText(fromProp);
      return;
    }
    const shownDigits = text.replace(/\D/g, '');
    const shownNumber = shownDigits === '' ? emptyValue : Number(shownDigits);
    const propNumber = value == null ? null : Math.round(Number(value));
    if ((shownNumber ?? null) !== (propNumber ?? null) && !(shownNumber === emptyValue && propNumber === emptyValue)) {
      setText(fromProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, showZeroAsEmpty, focused]);

  useLayoutEffect(() => {
    if (pendingCaret.current == null || !inputEl.current) return;
    const pos = pendingCaret.current;
    pendingCaret.current = null;
    try {
      inputEl.current.setSelectionRange(pos, pos);
    } catch {
      // Not a text-selection-capable input in this browser — ignore.
    }
  }, [text]);

  /** Apply a raw (possibly comma-laden) string with the caret at `caret`. */
  const applyRaw = useCallback(
    (raw: string, caret: number) => {
      const next = computeNextCurrencyText(raw, caret);
      pendingCaret.current = next.caret;
      setText(next.formatted);
      onChange(next.digits === '' ? emptyValue : Number(next.digits));
    },
    [onChange, emptyValue]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyRaw(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    inputProps?.onKeyDown?.(e as any);
    const el = e.currentTarget;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start == null || end == null || start !== end) return;
    // Backspace over a comma → remove the digit before the comma instead.
    if (e.key === 'Backspace' && start > 0 && text[start - 1] === ',') {
      e.preventDefault();
      applyRaw(text.slice(0, start - 2) + text.slice(start), start - 2);
    }
    // Delete over a comma → remove the digit after the comma instead.
    if (e.key === 'Delete' && text[start] === ',' && start + 1 < text.length) {
      e.preventDefault();
      applyRaw(text.slice(0, start + 1) + text.slice(start + 2), start + 1);
    }
  };

  return (
    <InputBase
      {...rest}
      type="text"
      value={text}
      onChange={handleChange}
      onFocus={(e) => {
        setFocused(true);
        setText(formatValue(value, showZeroAsEmpty));
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        onBlur?.(e);
      }}
      inputRef={(el: HTMLInputElement | null) => {
        inputEl.current = el;
        if (typeof inputRefProp === 'function') inputRefProp(el);
        else if (inputRefProp && 'current' in inputRefProp) (inputRefProp as React.MutableRefObject<any>).current = el;
      }}
      inputProps={{
        inputMode: 'numeric',
        ...inputProps,
        onKeyDown: handleKeyDown,
      }}
    />
  );
};

export default CurrencyTextInput;
